import React, {useEffect, useState, useRef} from 'react';

import {useTranslation} from 'react-i18next';
import {AnySchema, ValidationError} from 'yup';

import {getTemplate} from 'components/pages/Registries/mock';
import {readXlsx} from 'utils/readXlsx';

import {ErrorList, ErrorItem, Root, CustomLink, CustomFileInput, CustomLinkAction} from './styled';

interface MapObject {
    [key: string]: string;
}

export interface FileFormPropsCallback {
    file?: File | null;
    data: Array<MapObject>;
    errors?: Array<string>;
    isValid?: boolean;
}
export interface FileFormProps {
    onChange(data: FileFormPropsCallback): void;
    scheme?: AnySchema;
    assessors?: MapObject;
    initialValue?: File | null;
    linkExample?: string;
    linkExampleAction?: (link: string) => void;
    errors?: XLSXError[];
    serverValidate?: boolean;
    horizontal?: boolean;
}

export type XLSXError = {field: string; error: string};

export const mapByAssessors = (array: Array<MapObject>, assessors: MapObject) =>
    array.map((item: MapObject) => {
        const result: MapObject = {};
        Object.keys(assessors).forEach((name: string) => {
            result[name] = item[assessors[name]];
        });
        return result;
    });

export const mapByTemplate = (array: string[], file_type: string) => {
    const template = getTemplate(file_type);
    const templateValues = Object.values(template);
    console.log(templateValues);
    console.log(array);
    return array.reduce((acc, item, index) => {
        Object.assign(acc, {[templateValues[index]]: item});
        return acc;
    }, {});
};

export const XlsxFileForm = ({
    onChange,
    scheme,
    initialValue,
    assessors,
    linkExample,
    linkExampleAction,
    errors: errorsList,
    serverValidate,
    horizontal,
}: FileFormProps) => {
    const {t, i18n} = useTranslation();
    const didMountRef = useRef(false);
    const [file, fileChanged] = useState<File | null>(initialValue || null);
    const [errors, errorsChanged] = useState<Array<string> | null>(null);
    const [data, dataChanged] = useState<Array<MapObject>>([]);

    const handleChangeFile = (evt: any): void => {
        const {file} = readXlsx(evt, (data: any, _: File, file_type: string) => {
            const mappedData = mapByTemplate(data, file_type);
            console.log(mappedData);
            dataChanged(assessors ? mapByAssessors(data, assessors) : data);
        });

        if (serverValidate) return fileChanged(file);

        if (file) {
            fileChanged(file);
        } else {
            fileChanged(null);
            dataChanged([]);
        }
    };

    useEffect(() => {
        if (!serverValidate && scheme && file && didMountRef.current) {
            scheme
                .validate(data, {abortEarly: false})
                .then(() => {
                    errorsChanged([]);
                })
                .catch((err) => {
                    const errors: Array<string> = [];
                    err.inner.forEach((e: ValidationError) => {
                        const path = e.path ? e.path.match(/\[(.*)\]/) ?? [] : [];
                        const stringCount = path.pop();
                        errors.push(
                            stringCount
                                ? `${t('components.xlsxForm.row')}: ${
                                      stringCount ? parseInt(stringCount, 10) + 2 : '-'
                                  }: ` + (i18n.exists(e.message) ? t(e.message) : e.message)
                                : e.message,
                        );
                    });
                    console.log(errors);
                    errorsChanged(errors);
                });
        } else if (didMountRef.current && !file) {
            errorsChanged(null);
        }
    }, [data]);

    useEffect(() => {
        if (!file && didMountRef.current) {
            dataChanged([]);
        }
    }, [file]);

    useEffect(() => {
        if (didMountRef.current) {
            onChange({
                file,
                data,
                errors: errors === null ? [] : errors,
                isValid: errors !== null && !errors.length,
            });
        } else {
            didMountRef.current = true;
        }
    }, [file, data, errors]);

    return (
        <>
            <Root horizontal={horizontal}>
                <CustomFileInput
                    type="file"
                    label={t('components.form.uploadButton')}
                    initialValue={file?.name}
                    onChange={handleChangeFile}
                    accept=".xlsx,.xls"
                />
                {!file && linkExample && !linkExampleAction ? (
                    <CustomLink href={linkExample} tabIndex={-1} target="_blank" rel="noreferrer">
                        {t('components.xlsxForm.example')}
                    </CustomLink>
                ) : !file && linkExample && linkExampleAction ? (
                    <CustomLinkAction onClick={() => linkExampleAction(linkExample)} tabIndex={-1}>
                        {t('components.xlsxForm.example')}
                    </CustomLinkAction>
                ) : null}
            </Root>

            {serverValidate ? (
                <ErrorList>
                    {errorsList?.map(({error}, i) => (
                        <ErrorItem key={i}>{error}</ErrorItem>
                    ))}
                </ErrorList>
            ) : (
                <ErrorList>
                    {errors?.map((err, i) => (
                        <ErrorItem key={i}>{err}</ErrorItem>
                    ))}
                </ErrorList>
            )}
        </>
    );
};
